/*
 * The one state the application has no screen for: the boot itself failed.
 *
 * Everything a reader is normally told — a toast, the connection banner, a route's error state —
 * needs the shell, and the shell needs the boot. Between `index.html` and the first render the page
 * depends on the entry chunk, on the session request and on every module's routes and copy, which
 * are chunks of their own (`routes/router.tsx`); any one of them failing left the silhouette of
 * `frame.css` standing for ever, with nothing in the window and one `TypeError` in a console nobody
 * has open. A failed module fetch is not even retried by the browser: Chromium caches the failure
 * for the document's lifetime, so a retry inside the router would fetch nothing. Reloading is the
 * whole remedy, and this says so.
 *
 * It is plain DOM and not React on purpose: it must be able to paint when the reason the boot
 * failed is that a chunk of this build could not be fetched, and it lives in the entry chunk, where
 * every byte is measured (`scripts/checkBudgets.ts`).
 */

import { t } from "../i18n/index.ts"
import "./frame.css"

/**
 * Draws the failure line in place of the silhouette — but only while the silhouette is what stands
 * in the window.
 *
 * The boot can also fail *after* the first render: the database, the socket and the catch-up all
 * come after it, and by then the reader is looking at the application, which says what is wrong in
 * its own surfaces (the connection banner, a screen's error state). Replacing that with a sentence
 * would take a working window away, so the marker for "nothing has rendered yet" is the silhouette
 * itself: `createRoot().render()` replaces the container's children, so `.tb-frame` is there while
 * and only while React has not drawn anything.
 */
export function renderBootFailure(): void {
	const root = document.getElementById("root")
	if (!root) return
	if (root.childElementCount > 0 && root.querySelector(".tb-frame") === null) return

	const line = document.createElement("p")
	line.className = "tb-boot-failed"
	// A sentence that appears without anybody asking for it, in a document whose live regions do
	// not exist yet: `alert` is the one role that is read the moment it is inserted.
	line.setAttribute("role", "alert")
	line.textContent = t("shell.boot.failed")
	root.replaceChildren(line)
}

/**
 * How long a boot may take before the window says something. It is not a budget — a boot is about
 * a second on a cold cache — but the point by which a reader looking at a grey rectangle has
 * learnt that it is not going to become anything.
 */
export const BOOT_GIVE_UP_MS = 20_000

/**
 * Runs the boot, and draws the failure line if it fails **or if it never finishes**.
 *
 * A rejection is only half of what a broken boot looks like. A session request that is never
 * answered, a chunk whose response never completes, a dead connection the browser does not give up
 * on: none of them rejects, and each leaves the silhouette standing with no word in it — the exact
 * symptom this state exists for. So the line is drawn on a deadline as well, and a boot that
 * arrives clears it.
 *
 * Drawing it late is safe, and that is what keeps this to a timer: `renderBootFailure` paints only
 * while the silhouette is what stands in the window, and `start()` renders the application halfway
 * through its own work — so a boot that is merely slow after the first render is never written
 * over.
 */
export function bootOrSayWhy(
	start: () => Promise<unknown>,
	giveUpAfterMs: number = BOOT_GIVE_UP_MS,
): void {
	const giveUp = setTimeout(renderBootFailure, giveUpAfterMs)
	void start().then(
		() => clearTimeout(giveUp),
		() => {
			clearTimeout(giveUp)
			renderBootFailure()
		},
	)
}
